import { NavigationTabs } from '@monorepo/shared/componentsV2/NavigationTabs';
import { useSubnav } from '@monorepo/shared/hooks/subnav/useSubnav';
import * as theme from '@monorepo/shared/themes/default';
import classnames from 'classnames';
import React, { Fragment, ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { VisuallyHidden } from '../VisuallyHidden';
import {
  Drawer,
  DrawerHeader,
  SectionDivider,
  SectionTitle,
  TabContents,
  TabIcon,
} from './styled';

type Option = {
  href: string;
  isLegacy?: boolean;
  label: string;
  subtext?: string;
  icon?: ReactElement;
};

export type OptionSection = {
  title?: string;
  options: Option[];
  ariaLabel?: string;
};

interface SubnavBaseProps {
  isLoading?: boolean;
  optionSections: OptionSection[];
}

type SubnavWithLabelProps = SubnavBaseProps & {
  label: string;
};

type SubnavWithHeaderProps = SubnavBaseProps & {
  navAriaLabel: string;
  header: ReactElement;
};

const StyledNavigationTabs = styled(NavigationTabs)`
  padding: 8px 0;
`;

function hasComplexHeader(
  props: SubnavWithLabelProps | SubnavWithHeaderProps,
): props is SubnavWithHeaderProps {
  return 'header' in props;
}

function getLabel(label: string, subtext?: string, icon?: ReactElement) {
  return ({ isActive }: { isActive: boolean }) => (
    <TabContents className={classnames({ selected: isActive })}>
      {icon && <TabIcon aria-hidden>{icon}</TabIcon>}
      {subtext ? (
        <div>
          <div>{label}</div>
          <div className="subtext">{subtext}</div>
        </div>
      ) : (
        label
      )}
      {isActive && <VisuallyHidden>Selected</VisuallyHidden>}
    </TabContents>
  );
}

/**
 *
 * When collapsing, use along with `PageWithCollapsibleSubnav`
 */
export function Subnav(props: SubnavWithHeaderProps): ReactElement;
export function Subnav(props: SubnavWithLabelProps): ReactElement;
export function Subnav(
  props: SubnavWithHeaderProps | SubnavWithLabelProps,
): ReactElement | null {
  const { isLoading, optionSections } = props;
  const { isSubnavOpen, isSubnavDisabled } = useSubnav();

  const mappedSections = useMemo(
    () =>
      optionSections.map((section) => ({
        ...section,
        options: section.options.map((o) => ({
          ...o,
          label: getLabel(o.label, o.subtext, o.icon),
          skeletonWidth: `${o.label.length || 0}rem`,
        })),
      })),
    [optionSections],
  );

  let headerContent: string | ReactElement;
  let navAriaLabel: string;

  if (hasComplexHeader(props)) {
    const { header, navAriaLabel: navAriaLabelFromProps } = props;
    headerContent = header;
    navAriaLabel = navAriaLabelFromProps;
  } else {
    const { label } = props;
    headerContent = label;
    navAriaLabel = label;
  }

  if (isSubnavDisabled) {
    return null;
  }

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={isSubnavOpen}
      transitionDuration={theme.constants.subnavTransitionDuration}
    >
      <DrawerHeader>{headerContent}</DrawerHeader>
      {mappedSections.map(
        ({ title, options, ariaLabel: sectionAriaLabel }, index) => {
          const sectionSuffix = sectionAriaLabel ?? title;
          const label = sectionSuffix
            ? `${navAriaLabel} - ${sectionSuffix}`
            : navAriaLabel;

          return (
            <Fragment key={sectionAriaLabel ?? index}>
              {!isLoading && index > 0 && <SectionDivider />}
              {!isLoading && title && <SectionTitle>{title}</SectionTitle>}
              <StyledNavigationTabs
                isLoading={isLoading}
                label={label}
                options={options}
                orientation="vertical"
              />
            </Fragment>
          );
        },
      )}
    </Drawer>
  );
}
